import { useState, useEffect } from 'react';
import { shopAPI } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const EVIL_CONTAINMENT_ICON = "/api/static/shop_images/evil_containment_wave.png";

// Format seconds into readable time
const formatTimeRemaining = (seconds) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
};

export const AdFreeIndicator = () => {
  const { user } = useAuth();
  const [adFreeData, setAdFreeData] = useState({ active: false, remaining_seconds: 0 });

  useEffect(() => {
    if (!user) {
      setAdFreeData({ active: false, remaining_seconds: 0 });
      // Clear global flag when logged out
      window.__adFreeActive = false;
      if (window.__updateAdVisibility) window.__updateAdVisibility();
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await shopAPI.getAdFreeStatus();
        setAdFreeData(response.data);
        
        // Set global flag and update visibility
        window.__adFreeActive = response.data.active;
        if (window.__updateAdVisibility) {
          window.__updateAdVisibility();
        }
      } catch (error) {
        console.error('Failed to check ad-free status:', error);
        window.__adFreeActive = false;
        if (window.__updateAdVisibility) window.__updateAdVisibility();
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [user]);

  // Don't render if not active
  if (!adFreeData.active || adFreeData.remaining_seconds <= 0) {
    return null;
  }

  // Calculate days remaining
  const daysRemaining = Math.ceil(adFreeData.remaining_seconds / 86400);

  const handleClick = () => {
    const timeStr = formatTimeRemaining(adFreeData.remaining_seconds);
    toast.info(`${timeStr} remaining until the seal breaks!`, {
      icon: <img src={EVIL_CONTAINMENT_ICON} alt="" className="w-5 h-5" />,
      duration: 4000,
    });
  };

  return (
    <div 
      className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 cursor-pointer hover:border-purple-400/50 transition-colors"
      title={`Ad-Free: ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`}
      onClick={handleClick}
    >
      <img 
        src={EVIL_CONTAINMENT_ICON} 
        alt="Ad-Free" 
        className="w-5 h-5 object-contain"
      />
      <span className="text-xs font-bold text-purple-300">
        {daysRemaining}d
      </span>
    </div>
  );
};

export default AdFreeIndicator;
